/**
 * presets.js — export TARGETS, not codecs.
 *
 * WHY. The old picker was a row of five buttons reading «WebM α · ProRes ·
 * WebP · APNG · MP4». Every one of those names answers a question the person
 * did not ask. They came to put a cut-out subject somewhere — over a stream,
 * into a timeline, onto a page, into a chat — and only the answer to THAT
 * decides the container, the codec, the size, the frame rate and whether audio
 * belongs in the file at all. A picker made of codec names asks the visitor to
 * do the mapping, and the two ways they can get it wrong are both silent:
 *
 *   • «Transparent» + MP4 — H.264 has no alpha channel, so the file comes out
 *     opaque with the subject burned onto whatever the foreground happened to
 *     be. Nothing warned; the old UI let the pair be chosen freely.
 *   • WebM α in Firefox or Safari — our only working alpha-WebM encoder is
 *     MediaRecorder, which is Chromium-only (measured: alpha is flattened in
 *     Firefox, and WebCodecs cannot encode alpha in any browser — probed
 *     2026-09-01, `Alpha encoding is not currently supported`). The default
 *     format was therefore the one format some visitors could not produce.
 *
 * A target carries its own constraints, so both failures become impossible to
 * express rather than merely discouraged: `alpha` says whether the target can
 * carry transparency at all, and `available()` asks the browser — not a
 * user-agent string — whether it can be produced here.
 *
 * TELEGRAM. Its video-sticker rules are exact and public (core.telegram.org):
 * WEBM/VP9 with alpha, at most 512×512 with one side exactly 512, at most 3 s,
 * 30 fps, no audio stream, ≤256 KB. MediaRecorder picks VP9 when it is offered
 * first, which is what makes this target producible at all — the FFmpeg path
 * cannot encode VP9 in our build.
 *
 * Public API — window.VbrPresets:
 *   list()                        → target descriptors, in menu order
 *   get(id)                       → one descriptor
 *   available(id)                 → Promise<{ok:true} | {ok:false, reason}>
 *   fit(id, {w,h,fps,duration})   → { w, h, fps, duration, clamped:[…] }
 *   allowsTransparency(id)        → bool
 *   requiresBackground(id)        → bool
 */
(function (root) {
    'use strict';

    /* `alpha`     — the file can carry transparency
       `opaque`    — the file CANNOT, so a background must be chosen
       `maxSide`   — hard ceiling from the destination, not from our tier
       `maxDur`    — same
       `audio`     — false ⇒ the destination forbids an audio stream
       `pro`       — sold with Премиум (Фарид 2026-09-01)                     */
    var TARGETS = {
        overlay: {
            id: 'overlay', format: 'webm-alpha', ext: 'webm',
            alpha: true, audio: true, icon: 'layers',
        },
        editor: {
            id: 'editor', format: 'prores', ext: 'mov',
            alpha: true, audio: true, icon: 'film', pro: true,
        },
        sticker: {
            id: 'sticker', format: 'webm-alpha', ext: 'webm',
            alpha: true, audio: false, icon: 'sticker',
            maxSide: 512, exactSide: 512, maxDur: 3, fps: 30, maxBytes: 256 * 1024,
        },
        page: {
            id: 'page', format: 'webp', ext: 'webp',
            alpha: true, audio: false, icon: 'globe',
        },
        legacy: {
            id: 'legacy', format: 'apng', ext: 'png',
            alpha: true, audio: false, icon: 'image',
        },
        video: {
            id: 'video', format: 'mp4', ext: 'mp4',
            alpha: false, audio: true, icon: 'play',
        },
    };

    var ORDER = ['overlay', 'video', 'sticker', 'editor', 'page', 'legacy'];

    function list() { return ORDER.map(function (id) { return TARGETS[id]; }); }
    function get(id) { return TARGETS[id] || TARGETS.overlay; }
    function allowsTransparency(id) { return !!get(id).alpha; }
    function requiresBackground(id) { return !get(id).alpha; }

    /* Ask the browser, once, whether transparent WebM can actually be produced
       here. AlphaWebMRecorder.isSupported() is a real round-trip probe — it
       encodes two frames and reads a pixel back — so a "yes" is evidence, not a
       capability string. Everything else goes through FFmpeg-WASM and works
       wherever the tool runs at all. */
    /* No cache of our own on purpose. The primitive already caches a real
       verdict for the session and deliberately does NOT cache a measurement
       that failed to come off; a second cache here would freeze exactly the
       answer that was meant to be re-askable — which is what it did: the probe
       running during page load reported `probe-unplayable`, the same call a
       second later reported VP9 alpha, and the visitor kept the first one. */
    async function webmAlphaOk() {
        try {
            if (!root.AlphaWebMRecorder) return { ok: false, reason: 'no-module' };
            var s = await root.AlphaWebMRecorder.isSupported({ fps: 30 });
            return (s && s.ok) ? { ok: true, codec: s.codec } : { ok: false, reason: (s && s.reason) || 'no-alpha' };
        } catch (e) { return { ok: false, reason: 'probe-failed' }; }
    }

    /* Reasons the BROWSER gave. Anything else is "the measurement did not come
       off", which is not the same answer and must not close a door. */
    var CONCLUSIVE = { 'alpha-flattened': 1, 'no-mediarecorder': 1, 'no-webm-mime': 1, 'no-requestframe': 1, 'no-module': 1 };

    async function available(id) {
        var t = get(id);
        if (t.format !== 'webm-alpha') return { ok: true, conclusive: true };
        var w = await webmAlphaOk();
        if (w.ok) return { ok: true, conclusive: true, codec: w.codec };
        return { ok: false, conclusive: !!CONCLUSIVE[w.reason], reason: w.reason };
    }

    /**
     * Fit a source clip to a target, reporting WHAT was clamped so the UI can
     * say so before the export rather than hand back a surprise. Silent
     * downscaling is the same defect class as the silent MP4-without-alpha it
     * replaces.
     */
    function fit(id, src) {
        var t = get(id);
        var w = src.w | 0, h = src.h | 0;
        var fps = src.fps || 30, dur = src.duration || 0;
        var clamped = [];

        if (t.exactSide && w > 0 && h > 0) {
            // Telegram wants ONE side exactly 512 and neither over it — that is
            // "fit inside 512 and scale the long side up to it", not "shrink".
            var k = t.exactSide / Math.max(w, h);
            var nw = Math.round(w * k), nh = Math.round(h * k);
            if (nw !== w || nh !== h) clamped.push('size');
            w = nw; h = nh;
        } else if (t.maxSide && Math.max(w, h) > t.maxSide) {
            var k2 = t.maxSide / Math.max(w, h);
            w = Math.round(w * k2); h = Math.round(h * k2);
            clamped.push('size');
        }
        if (w % 2) w--;
        if (h % 2) h--;

        if (t.fps && fps !== t.fps) { fps = t.fps; clamped.push('fps'); }
        if (t.maxDur && dur > t.maxDur) { dur = t.maxDur; clamped.push('duration'); }
        if (t.audio === false && src.hasAudio) clamped.push('audio');

        return { w: Math.max(2, w), h: Math.max(2, h), fps: fps, duration: dur, clamped: clamped };
    }

    root.VbrPresets = {
        list: list,
        get: get,
        available: available,
        fit: fit,
        allowsTransparency: allowsTransparency,
        requiresBackground: requiresBackground,
        TARGETS: TARGETS,
        ORDER: ORDER,
    };
})(typeof window !== 'undefined' ? window : globalThis);
